import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingCart, Copy, Check, Share2, Loader2, ArrowLeft, ArrowRight, CheckCircle2, Users, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  buildShoppingList, buildShoppingListForWeek, formatQty,
  type MealForShopping,
} from '@/lib/shoppingList';
import { recommendPurchase } from '@/lib/shoppingUnits';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  foodItems?: string[];
  clientId?: string;
  title?: string;
}

type Step = 'edit' | 'summary';

const PEOPLE_OPTIONS = [1, 2, 3] as const;
const DAYS_OPTIONS = [4, 5, 7] as const;

/**
 * Lista de compras con flujo de 2 pasos + selectores de personas/días.
 *
 *   Paso 1 (edit): marcas con check lo que YA TIENES en casa.
 *                  Arriba elegimos para cuántas personas y cuántos días.
 *   Paso 2 (summary): vista limpia con SOLO lo que vas a comprar,
 *                     ya traducido a unidades de súper (latas, piezas,
 *                     bolsas) usando el catálogo de shoppingUnits.
 *
 * Multiplicador final = (días / 7) × personas.
 * Para frutas/verduras frescas se agrega 1 pieza extra de margen.
 */
export function ShoppingListDialog({ open, onOpenChange, foodItems, clientId, title }: Props) {
  const [fetchedMeals, setFetchedMeals] = useState<MealForShopping[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('edit');
  const [people, setPeople] = useState<number>(1);
  const [days, setDays] = useState<number>(7);

  useEffect(() => {
    if (!open) return;
    setStep('edit');
    if (!clientId || foodItems) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('client_meal_plans')
        .select('food_item, day_of_week')
        .eq('client_id', clientId)
        .eq('is_active', true);
      if (cancelled) return;
      setFetchedMeals(
        (data || []).map((r: any) => ({
          food_item: r.food_item || '',
          day_of_week: r.day_of_week ?? null,
        })),
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, clientId, foodItems]);

  // Lista base = 7 días, 1 persona (lo que devuelve el builder).
  const baseList = useMemo(() => {
    if (foodItems) return buildShoppingList(foodItems, 1);
    if (fetchedMeals) return buildShoppingListForWeek(fetchedMeals);
    return { quantified: [], unquantified: [] };
  }, [foodItems, fetchedMeals]);

  // Multiplicador final que aplicamos a cada cantidad cuantificada.
  const multiplier = (days / 7) * people;

  // Cantidades escaladas por personas/días. Cada unidad de cada ingrediente
  // se multiplica.
  const items = useMemo(
    () =>
      baseList.quantified.map(it => ({
        ingredient: it.ingredient,
        units: it.units.map(u => ({ qty: u.qty * multiplier, unit: u.unit })),
      })),
    [baseList, multiplier],
  );
  const freeItems = baseList.unquantified;

  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => { setChecked(new Set()); }, [foodItems, fetchedMeals]);

  const toggle = (key: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toBuyQuantified = useMemo(
    () => items.filter(it => !checked.has(it.ingredient.toLowerCase())),
    [items, checked],
  );
  const toBuyFree = useMemo(
    () => freeItems.filter(f => !checked.has(`free::${f.toLowerCase()}`)),
    [freeItems, checked],
  );
  const totalToBuy = toBuyQuantified.length + toBuyFree.length;

  /** Para un item, devuelve la mejor representación: recomendación de
   *  compra (latas, piezas, bolsas) si el catálogo lo conoce, o la
   *  cantidad cruda formateada. */
  const renderItem = (
    ingredient: string,
    units: { qty: number; unit: string }[],
  ) => {
    // Usamos la primera unidad (la de mayor qty agregada — el builder
    // ya las ordena). Si una receta mezcla unidades, mostramos ambas.
    const primary = units[0];
    const rec = primary
      ? recommendPurchase(ingredient, primary.qty, primary.unit, { bufferPieces: 1 })
      : null;

    if (rec) {
      return {
        title: `${rec.emoji ? rec.emoji + ' ' : ''}${ingredient}`,
        detail: rec.label,
        hint: rec.hint,
        // Si hay unidades secundarias (ej. "1 taza" + "30 g"), las anotamos
        extras: units.slice(1).map(u => `${formatQty(u.qty)} ${u.unit}`),
      };
    }
    return {
      title: ingredient,
      detail: units.map(u => `${formatQty(u.qty)} ${u.unit}`).join(' + '),
      hint: undefined as string | undefined,
      extras: [] as string[],
    };
  };

  /** Texto que se manda al WhatsApp / portapapeles. Solo incluye items
   *  no marcados y usa la representación amigable (recomendación). */
  const buildText = () => {
    const subtitle = `_Para ${days} días · ${people} ${people === 1 ? 'persona' : 'personas'}_\n\n`;
    const header = title ? `🛒 Lista de compras — ${title}\n${subtitle}` : `🛒 Lista de compras\n${subtitle}`;
    const main = toBuyQuantified
      .map(it => {
        const r = renderItem(it.ingredient, it.units);
        const extraLine = r.extras.length ? ` (${r.extras.join(' + ')})` : '';
        const hintLine = r.hint ? ` — ${r.hint}` : '';
        return `${r.title}: ${r.detail}${hintLine}${extraLine}`;
      })
      .join('\n');
    const free = toBuyFree.length
      ? `\n\nAl gusto / sin cantidad fija:\n${toBuyFree.map(f => `• ${f}`).join('\n')}`
      : '';
    const footer = `\n\n📌 Cantidades para ${days} días, ${people} ${people === 1 ? 'persona' : 'personas'}. Frutas y verduras frescas incluyen 1 pieza extra de margen.`;
    return header + main + free + footer;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
      toast.success('Lista copiada al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleShare = async () => {
    const text = buildText();
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }
    try { await navigator.clipboard.writeText(text); } catch {}
    toast.info('Mensaje copiado, abriendo WhatsApp…');
    setTimeout(() => {
      window.location.href =
        'https://api.whatsapp.com/send?text=' + encodeURIComponent(text);
    }, 600);
  };

  const totalListed = items.length + freeItems.length;
  const totalChecked = totalListed - totalToBuy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-accent" />
            {step === 'edit' ? 'Lista de compras' : 'Resumen — lo que vas a comprar'}
          </DialogTitle>
          <DialogDescription>
            {step === 'edit' ? (
              <>
                Cantidades para <strong>{days} días</strong>, <strong>{people} {people === 1 ? 'persona' : 'personas'}</strong>.
                Marca lo que ya tengas en casa.
              </>
            ) : (
              <>
                Vas a comprar <strong>{totalToBuy} cosas</strong>
                {totalChecked > 0 && ` · ya tienes ${totalChecked} en casa`}.
                Revisa que esté bien antes de compartir.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Selectores de personas/días — solo en paso 1 */}
        {step === 'edit' && (
          <div className="space-y-2 pb-2">
            <p className="text-[11px] text-muted-foreground leading-relaxed px-1">
              ¿Compras para más personas o para más/menos días? Ajusta abajo y
              la lista se recalcula sola.
            </p>
            <div className="grid grid-cols-2 gap-3 px-1">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                <Users className="h-3 w-3" /> Personas
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {PEOPLE_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => setPeople(n)}
                    className={`min-w-[36px] h-9 px-2 rounded-lg text-xs font-black uppercase tracking-wider transition-colors ${
                      people === n ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground hover:bg-border'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={PEOPLE_OPTIONS.includes(people as any) ? '' : people}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 1 && v <= 20) setPeople(v);
                  }}
                  placeholder="+"
                  className="w-12 h-9 text-center px-1 text-xs font-black"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> Días
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => setDays(n)}
                    className={`min-w-[36px] h-9 px-2 rounded-lg text-xs font-black uppercase tracking-wider transition-colors ${
                      days === n ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground hover:bg-border'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={DAYS_OPTIONS.includes(days as any) ? '' : days}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 1 && v <= 30) setDays(v);
                  }}
                  placeholder="+"
                  className="w-12 h-9 text-center px-1 text-xs font-black"
                />
              </div>
            </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-1">
          {loading ? (
            <div className="py-12 flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-xs">Cargando plan…</span>
            </div>
          ) : items.length === 0 && freeItems.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No hay ingredientes detectables. Asegúrate de que cada comida tenga
              líneas en formato "* 1 taza de Arroz integral".
            </div>
          ) : step === 'edit' ? (
            <>
              {items.map(it => {
                const key = it.ingredient.toLowerCase();
                const isChecked = checked.has(key);
                const r = renderItem(it.ingredient, it.units);
                return (
                  <label
                    key={key}
                    className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                      isChecked ? 'bg-secondary/40 opacity-50' : 'hover:bg-secondary/40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(key)}
                      className="mt-1 shrink-0 w-4 h-4 accent-accent"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-bold ${isChecked ? 'line-through' : ''}`}>
                        {r.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {r.detail}
                        {r.hint && <span className="opacity-70"> · {r.hint}</span>}
                      </div>
                    </div>
                    {isChecked && <Check className="h-4 w-4 text-accent shrink-0 mt-1" />}
                  </label>
                );
              })}

              {freeItems.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 mb-1.5">
                    Al gusto · sin cantidad fija
                  </div>
                  {freeItems.map((f) => {
                    const key = `free::${f.toLowerCase()}`;
                    const isChecked = checked.has(key);
                    return (
                      <label
                        key={key}
                        className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          isChecked ? 'bg-secondary/40 opacity-50' : 'hover:bg-secondary/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(key)}
                          className="mt-1 shrink-0 w-4 h-4 accent-accent"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${isChecked ? 'line-through' : ''}`}>{f}</div>
                        </div>
                        {isChecked && <Check className="h-4 w-4 text-accent shrink-0 mt-1" />}
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {totalToBuy === 0 ? (
                <div className="py-12 flex flex-col items-center gap-2 text-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                  <p className="text-sm font-bold">¡Ya tienes todo en casa!</p>
                  <p className="text-xs text-muted-foreground">
                    No marcaste nada para comprar. Si te falta algo, regresa a editar la lista.
                  </p>
                </div>
              ) : (
                <>
                  {toBuyQuantified.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] font-black uppercase tracking-widest text-accent px-3 mb-1.5">
                        Para comprar ({toBuyQuantified.length})
                      </div>
                      {toBuyQuantified.map(it => {
                        const key = it.ingredient.toLowerCase();
                        const r = renderItem(it.ingredient, it.units);
                        return (
                          <div key={key} className="px-3 py-2 rounded-lg bg-secondary/30">
                            <div className="text-sm font-bold">{r.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {r.detail}
                              {r.hint && <span className="opacity-70"> · {r.hint}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {toBuyFree.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-border space-y-1">
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 mb-1.5">
                        Al gusto ({toBuyFree.length})
                      </div>
                      {toBuyFree.map(f => (
                        <div key={`free::${f}`} className="px-3 py-2 rounded-lg bg-secondary/30">
                          <div className="text-sm">{f}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      📌 Cantidades para <strong>{days} días</strong>, <strong>{people} {people === 1 ? 'persona' : 'personas'}</strong>. Frutas y verduras frescas incluyen 1 pieza extra de margen. Ajusta arriba si tu rutina cambia.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {step === 'edit' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => setStep('summary')}
                disabled={loading || (items.length === 0 && freeItems.length === 0)}
                className="gap-1.5"
              >
                Continuar <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('edit')} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" /> Editar lista
              </Button>
              <Button variant="outline" onClick={handleCopy} disabled={totalToBuy === 0} className="gap-1.5">
                <Copy className="h-4 w-4" /> Copiar
              </Button>
              <Button onClick={handleShare} disabled={totalToBuy === 0} className="gap-1.5">
                <Share2 className="h-4 w-4" /> Compartir
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
